import { clamp } from "lodash-es";
import { memo, type ReactElement, useCallback, useMemo } from "react";
import { HStack, NumberFormatField, TriggerButton, VStack } from "widgets";
import { useShallow } from "zustand/shallow";

import { MAX_BASE_PER_PIXEL, MIN_BASE_PER_PIXEL } from "../../settings.ts";
import { useGenomeViewerStore } from "../../store/store.ts";
import numberFieldStyle from "./ZoomControl.module.css";

const ZOOM_SCALE = 2 as const;

interface ZoomNumberFieldProps {
  onChange: (value: number) => void;
  value: number;
  minValue: number;
  maxValue: number;
}

const ZoomNumberField = ({ onChange, value, minValue, maxValue }: ZoomNumberFieldProps): ReactElement => {
  return (
    <HStack spacing={0} className={numberFieldStyle.container}>
      <NumberFormatField
        onValueChange={(e, sourceInfo) => {
          if (sourceInfo.source === "event" && e.floatValue !== undefined) {
            onChange(clamp(e.floatValue, minValue, maxValue));
          }
        }}
        decimalScale={2}
        aria-label={"Zoom"}
        key={"zoom-field"}
        value={clamp(value, minValue, maxValue)}
        spellCheck={false}
        autoCapitalize={"off"}
        autoCorrect={"off"}
        suffix={" Kb"}
        max={maxValue}
        min={minValue}
        allowNegative={false}
        thousandsGroupStyle={"thousand"}
        thousandSeparator={","}
        isAllowed={({ floatValue }) => floatValue !== undefined && floatValue >= minValue && floatValue <= maxValue}
        className={numberFieldStyle.inputField}
        data-testid="number-field"
      />
      <VStack spacing={0} className={numberFieldStyle.buttonGroup}>
        <TriggerButton
          tooltip={"Zoom Out"}
          icon={"arrow_drop_up"}
          aria-label="Increment"
          data-testid="number-field-step-up"
          label=""
          onPress={() => {
            onChange(Math.min(ZOOM_SCALE * value, maxValue));
          }}
          className={numberFieldStyle.button}
        />
        <TriggerButton
          tooltip={"Zoom In"}
          icon={"arrow_drop_down"}
          aria-label="Decrement"
          data-testid="number-field-step-down"
          label=""
          onPress={() => {
            onChange(Math.max(value / ZOOM_SCALE, minValue));
          }}
          className={numberFieldStyle.button}
        />
      </VStack>
    </HStack>
  );
};

export const ZoomControl = memo(function ZoomControl() {
  const { basePerPixel, canvasWidth } = useGenomeViewerStore(
    useShallow((state) => {
      return {
        basePerPixel: state.basePerPixel,
        canvasWidth: state.canvas?.width ?? 0,
      };
    }),
  );

  const minValue = useMemo(() => (MIN_BASE_PER_PIXEL * canvasWidth) / 1000, [canvasWidth]);
  const maxValue = useMemo(() => (MAX_BASE_PER_PIXEL * canvasWidth) / 1000, [canvasWidth]);
  const baseNumber = useMemo(() => (canvasWidth * basePerPixel) / 1000, [canvasWidth, basePerPixel]);
  const setZoom = useGenomeViewerStore((state) => state.setZoom);

  const handleZoomChange = useCallback(
    (newValue: number) => {
      setZoom((newValue * 1000) / canvasWidth);
    },
    [canvasWidth, setZoom],
  );

  return <ZoomNumberField onChange={handleZoomChange} value={baseNumber} maxValue={maxValue} minValue={minValue} />;
});
